import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useLocation } from 'wouter'
import { CheckCircle, Zap, Users, FileText, TrendingUp } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'

export function Welcome() {
  const [, setLocation] = useLocation()
  const { user } = useAuth()

  const handleGetStarted = () => {
    setLocation('/')
  }

  const handleCreateRfp = () => {
    setLocation('/create-rfp')
  }

  const handleBrowseRfps = () => {
    setLocation('/request-for-proposal-marketplace')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 p-4">
      <div className="max-w-4xl mx-auto py-8">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome to SourcedIn, {user?.name?.split(' ')[0] || 'there'}! 🎉
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Your LinkedIn account has been connected successfully
          </p>
          <p className="text-lg text-gray-500">
            You're now ready to explore the world's best RFP marketplace
          </p>
        </div>

        {/* Features Overview */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-2">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle className="text-lg">Create RFPs</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Post your project requirements and get proposals from qualified professionals
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-2">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle className="text-lg">Find Opportunities</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Browse and respond to RFPs that match your expertise and interests
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-2">
                <Zap className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-lg">AI Assistant</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Get help writing compelling RFPs and responses with our AI assistant
              </CardDescription>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardHeader>
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-lg">Track Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Monitor your RFPs and responses with detailed analytics and insights
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Next Steps */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-2xl text-center">What would you like to do first?</CardTitle>
            <CardDescription className="text-center text-lg">
              Choose your path to get started on SourcedIn
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleCreateRfp}
                size="lg"
                className="flex-1 max-w-xs"
              >
                <FileText className="w-5 h-5 mr-2" />
                Post an RFP
              </Button>
              <Button 
                onClick={handleBrowseRfps}
                variant="outline"
                size="lg"
                className="flex-1 max-w-xs"
              >
                <Users className="w-5 h-5 mr-2" />
                Browse Opportunities
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-center">Join Our Growing Community</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">500+</div>
                <div className="text-sm text-gray-600">Active Professionals</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">1000+</div>
                <div className="text-sm text-gray-600">RFPs Posted</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">$2M+</div>
                <div className="text-sm text-gray-600">Projects Matched</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Continue Button */}
        <div className="text-center mt-8">
          <Button 
            onClick={handleGetStarted}
            variant="ghost"
            className="text-gray-600 hover:text-gray-800"
          >
            Skip and go to dashboard →
          </Button>
        </div>
      </div>
    </div>
  )
}
